import React, { memo, useMemo } from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { DisaggregatedMetric } from "../../services/types";
import { getMetricGroupChartId } from "../../utils/dashboardUtils";
import { formatValue } from "../../utils/helpers";

interface PDFSimpleBarChartProps {
    indicators: DisaggregatedMetric[] | null;
    title?: string;
    tab: string;
    metric: DisaggregatedMetric | undefined;
    icon?: React.FC<{ width?: number; height?: number }> | null;
    chartName: string;
    barColor: string;
    reverseOrder: boolean;
    topFive?: boolean;
    sort?: string;
}

const normalizeLabel = (label: string) =>
    label
        .replace(/[\u00A0\u202F]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const styles = StyleSheet.create({
    container: { width: '48%' },
    barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
    label: { width: 80, fontSize: 8, color: '#3A2E29' },
    value: { width: 20, fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#3A2E29', marginLeft: 8 },
    barBackground: { flex: 1, height: 10, backgroundColor: '#FFFFFF', marginLeft: 4, position: 'relative' },
    barFill: { height: 10 },
    metricCard: {
        width: '48%',
        marginBottom: 16,
        paddingRight: 4,
    },
    metricValue: { fontSize: 20, fontWeight: 700 },
    metricTitle: { fontSize: 10, fontWeight: 700, color: '#3A2E29' },
    metricYear: { fontSize: 10, marginTop: 2, marginBottom: 8, color: '#3A2E29' },
});

const PDFSimpleBarChart = memo(({ indicators, tab, chartName, barColor, reverseOrder, topFive, sort, metric, icon, title }: PDFSimpleBarChartProps) => {
    const data = useMemo(() => {
        if (!indicators) return [];
        const indicatorData = indicators.find(d => d.id === getMetricGroupChartId(tab, chartName));
        if (!indicatorData) return [];
        const version = indicatorData.versions[0];
        if (!version) return [];

        const tallyMap = new Map<string, number>();
        version.disaggregations.forEach(d => {
            let value: number;
            switch (version.unit) {
                case 'integer':
                    value = version.value ? d.value / version.value : 0;
                    break;
                case 'percentage':
                case 'decimal':
                case 'ratio':
                    value = d.value;
                    break;
                default:
                    value = 0;
            }
            tallyMap.set(normalizeLabel(d.name), value);
        });

        let allValues = Array.from(tallyMap.entries()).map(([label, value]) => ({ label, value }));
        if (sort === 'alphabetical') {
            allValues = allValues.sort((a, b) => a.label.localeCompare(b.label));
        }
        if (topFive) {
            allValues = allValues.sort((a, b) => b.value - a.value).slice(0, 6);
        }
        if (reverseOrder) {
            allValues.reverse();
        }
        return allValues;
    }, [indicators, tab, chartName, reverseOrder, topFive, sort]);

    if (!metric) return;

    const { name, versions } = metric;
    const { year } = versions[0];

    const maxValue = Math.max(...data.map(d => d.value)) || 1;

    return (
        <View style={styles.metricCard}>
            {icon && React.createElement(icon, { width: 30, height: 30 })}
            <Text style={styles.metricTitle}>{title ?? name}</Text>
            <Text style={styles.metricYear}>in {year}</Text>
            {data.map(d => (
                <View key={d.label} style={styles.barRow}>
                    <Text style={styles.label}>{d.label}</Text>
                    <Text style={styles.value}>{formatValue(d.value, 'percentage', metric.id)}</Text>
                    <View style={styles.barBackground}>
                        <View style={[styles.barFill, { width: `${(d.value / maxValue) * 100}%`, backgroundColor: barColor }]} />
                    </View>
                </View>
            ))}
        </View>
    );
});

export default PDFSimpleBarChart;